import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { getApiErrorMessage, toastError } from './toast';

/**
 * Shared QueryClient. Global error handlers surface failures as toasts so every
 * page gets consistent error feedback without bespoke try/catch in each call.
 *
 * The axios response interceptor still owns the 401/403 -> logout flow; those
 * are intentionally NOT re-toasted here to avoid a flash before redirect.
 */
const isAuthError = (error: unknown) => {
  const status = (error as any)?.response?.status;
  return status === 401 || status === 403;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isAuthError(error)) return;
      // Pages with background auto-refresh (e.g. Safety/SOS) opt out of global toasts
      // via meta and render their own inline error state — otherwise every failed
      // poll cycle stacks another toast.
      if (query.meta?.suppressErrorToast) return;
      toastError(getApiErrorMessage(error, 'Failed to load data'));
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (isAuthError(error)) return;
      if (mutation.meta?.suppressErrorToast) return;
      toastError(getApiErrorMessage(error, 'Action failed'));
    },
  }),
});
